import { create } from 'zustand';
import { Promotion, Screen, DisplayConfig, Layout, LayoutType, ScreenSequence, SequenceConfig, ScreenSequenceItem, ScheduleType } from '../shared/types';

interface Store {
  // Data
  promotions: Promotion[];
  screens: Screen[];
  displayConfig: DisplayConfig;
  showHeader: boolean;

  // Novas propriedades para sequência
  sequences: ScreenSequence[];
  sequenceConfig: SequenceConfig;
  currentScreenIndex: number; // Índice da tela atual na sequência ativa

  // Actions
  addPromotion: (promotion: Omit<Promotion, 'id' | 'createdAt'>, id?: number) => void;
  updatePromotion: (id: number, promotion: Partial<Promotion>) => void;
  deletePromotion: (id: number) => void;

  addScreen: (screen: Omit<Screen, 'id' | 'createdAt'>) => void;
  updateScreen: (id: number, screen: Partial<Screen>) => void;
  deleteScreen: (id: number) => void;

  setActiveScreen: (screenId: number | null) => void;
  updateDisplayConfig: (config: Partial<DisplayConfig>) => void;
  setShowHeader: (show: boolean) => void;

  // Novos métodos para sequência
  addSequence: (sequence: Omit<ScreenSequence, 'id' | 'createdAt'>) => void;
  updateSequence: (id: number, sequence: Partial<ScreenSequence>) => void;
  deleteSequence: (id: number) => void;
  setActiveSequence: (sequenceId: number | null) => void;
  updateSequenceConfig: (config: Partial<SequenceConfig>) => void;
  advanceToNextScreen: () => void;
  goToPreviousScreen: () => void;
  goToSpecificScreen: (index: number) => void;
}

export const layouts: Layout[] = [
  { id: 'simple2col', name: 'Lista - Layout com colunas customizáveis', description: 'Lista com colunas customizáveis (1, 2 ou 3 colunas) com preços normais' },
  { id: 'panfleto', name: 'Panfleto - Cartaz de produtos', description: 'Exibe um produto por vez em tela cheia; vários produtos alternam no tempo configurado' },
  { id: 'video', name: 'Vídeo', description: 'Exibe um vídeo promocional em tela cheia' }
];

export const useStore = create<Store>((set, get) => {
  return {
  promotions: [],
  screens: [],
  displayConfig: {
    activeScreenId: null,
    refreshInterval: 30,
    transitionEffect: 'fade',
    showHeaderInFullscreen: false,
    zoomLevel: 1 // Initial zoom level (1 = 100%)
  },
  showHeader: true,

  // Novas propriedades para sequência
  sequences: [],
  sequenceConfig: {
    activeSequenceId: null,
    scheduleType: 'sequence',
    autoAdvance: true
  },
  currentScreenIndex: 0,

  addPromotion: (promotion, id) => set((state) => ({
    promotions: [...state.promotions, {
      ...promotion,
      id: id || Date.now(),
      createdAt: new Date().toISOString()
    }]
  })),

  updatePromotion: (id, promotion) => set((state) => ({
    promotions: state.promotions.map(p =>
      p.id === id ? { ...p, ...promotion } : p
    )
  })),

  deletePromotion: (id) => set((state) => ({
    promotions: state.promotions.filter(p => p.id !== id),
    screens: state.screens.map(screen => ({
      ...screen,
      promotions: screen.promotions.filter(promoId => promoId !== id),
      linkedProducts: (screen.linkedProducts || []).filter(p => p.id !== id)
    }))
  })),

  addScreen: (screen) => set((state) => ({
    screens: [...state.screens, {
      ...screen,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      linkedProducts: screen.linkedProducts || []
    }]
  })),

  updateScreen: (id, screen) => set((state) => {
    const existingScreen = state.screens.find(s => s.id === id);
    let finalPromotions: number[] = [];
    let finalLinkedProducts = existingScreen?.linkedProducts || [];
    
    if (!('promotions' in screen) && existingScreen) {
      finalPromotions = Array.isArray(existingScreen.promotions) ? existingScreen.promotions : [];
    } else if ('promotions' in screen && screen.promotions !== undefined) {
      finalPromotions = Array.isArray(screen.promotions) ? screen.promotions : [];
    } else {
      finalPromotions = [];
    }
    
    if ('linkedProducts' in screen && screen.linkedProducts !== undefined) {
      finalLinkedProducts = Array.isArray(screen.linkedProducts) ? screen.linkedProducts : [];
    }
    
    return {
      screens: state.screens.map(s => {
        if (s.id === id) {
          return { 
            ...s, 
            ...screen,
            promotions: finalPromotions,
            linkedProducts: finalLinkedProducts
          } as Screen;
        }
        return s;
      })
    };
  }),

  deleteScreen: (id) => set((state) => {
    const updatedScreens = state.screens.filter(s => s.id !== id);
    const updatedSequences = state.sequences.map(seq => ({
      ...seq,
      screens: seq.screens.filter(item => item.screenId !== id)
    }));
    const updatedDisplayConfig = state.displayConfig.activeScreenId === id
      ? { ...state.displayConfig, activeScreenId: null }
      : state.displayConfig;

    return {
      screens: updatedScreens,
      sequences: updatedSequences,
      displayConfig: updatedDisplayConfig
    };
  }),

  setActiveScreen: (screenId) => set((state) => ({
    displayConfig: { ...state.displayConfig, activeScreenId: screenId }
  })),

  updateDisplayConfig: (config) => set((state) => ({
    displayConfig: { ...state.displayConfig, ...config }
  })),

  setShowHeader: (show) => set(() => ({ showHeader: show })),

  // Novos métodos para sequência
  addSequence: (sequence) => set((state) => ({
    sequences: [...state.sequences, {
      ...sequence,
      id: Date.now(),
      createdAt: new Date().toISOString()
    }]
  })),

  updateSequence: (id, sequence) => set((state) => ({
    sequences: state.sequences.map(s =>
      s.id === id ? { ...s, ...sequence } : s
    )
  })),

  deleteSequence: (id) => set((state) => {
    // Update sequenceConfig if needed
    const updatedSequenceConfig = state.sequenceConfig.activeSequenceId === id
      ? { ...state.sequenceConfig, activeSequenceId: null }
      : state.sequenceConfig;

    return {
      sequences: state.sequences.filter(s => s.id !== id),
      sequenceConfig: updatedSequenceConfig,
      currentScreenIndex: 0
    };
  }),

  setActiveSequence: (sequenceId) => set((state) => {
    const sequence = state.sequences.find(s => s.id === sequenceId);

    // Se a sequência existe e tem telas
    if (sequence && sequence.screens.length > 0) {
      // Ordenar as telas por ordem
      const orderedScreens = [...sequence.screens].sort((a, b) => a.order - b.order);

      // Definir a primeira tela da sequência como ativa
      const firstScreenId = orderedScreens[0].screenId;

      return {
        sequenceConfig: { ...state.sequenceConfig, activeSequenceId: sequenceId },
        displayConfig: { ...state.displayConfig, activeScreenId: firstScreenId },
        currentScreenIndex: 0
      };
    }

    return {
      sequenceConfig: { ...state.sequenceConfig, activeSequenceId: sequenceId },
      currentScreenIndex: 0
    };
  }),

  updateSequenceConfig: (config) => set((state) => ({
    sequenceConfig: { ...state.sequenceConfig, ...config }
  })),

  advanceToNextScreen: () => set((state) => {
    const { sequences, sequenceConfig, currentScreenIndex } = state;
    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);

    if (!activeSequence || activeSequence.screens.length === 0) {
      return state;
    }

    // Ordenar as telas por ordem
    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);

    // Calcular o próximo índice, voltando ao início se necessário
    const nextIndex = (currentScreenIndex + 1) % orderedScreens.length;
    const nextScreenId = orderedScreens[nextIndex].screenId;

    return {
      displayConfig: { ...state.displayConfig, activeScreenId: nextScreenId },
      currentScreenIndex: nextIndex
    };
  }),

  goToPreviousScreen: () => set((state) => {
    const { sequences, sequenceConfig, currentScreenIndex } = state;
    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);

    if (!activeSequence || activeSequence.screens.length === 0) {
      return state;
    }

    // Ordenar as telas por ordem
    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);

    // Calcular o índice anterior, indo para o último se estiver no início
    const previousIndex = currentScreenIndex === 0
      ? orderedScreens.length - 1
      : currentScreenIndex - 1;
    const previousScreenId = orderedScreens[previousIndex].screenId;

    return {
      displayConfig: { ...state.displayConfig, activeScreenId: previousScreenId },
      currentScreenIndex: previousIndex
    };
  }),

  goToSpecificScreen: (index) => set((state) => {
    const { sequences, sequenceConfig } = state;
    const activeSequence = sequences.find(s => s.id === sequenceConfig.activeSequenceId);

    if (!activeSequence || activeSequence.screens.length === 0 || index < 0 || index >= activeSequence.screens.length) {
      return state;
    }

    // Ordenar as telas por ordem
    const orderedScreens = [...activeSequence.screens].sort((a, b) => a.order - b.order);
    const screenId = orderedScreens[index].screenId;

    return {
      displayConfig: { ...state.displayConfig, activeScreenId: screenId },
      currentScreenIndex: index
    };
  })
  };
}); 